import React from "react";
import { useParams } from "react-router-dom";

import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

import StandardFileViewerPage from "./StandardFileViewerPage";

function getFileExtension(file) {
    const name = file.displayName || file.filename || "";
    const cleanName = name.split("?")[0];
    const lastDot = cleanName.lastIndexOf(".");

    if (lastDot <= 0 || lastDot === cleanName.length - 1) {
        return "FILE";
    }

    return cleanName.slice(lastDot + 1).toUpperCase();
}

function MiscFilePreview({ file }) {
    const fileExtension = getFileExtension(file);

    return (
        <Box
            sx={{
                minHeight: { xs: 300, sm: 410 },
                px: { xs: 2.5, sm: 4 },
                py: { xs: 4, sm: 6 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "radial-gradient(circle at 50% 36%, rgba(31, 184, 94, 0.15), transparent 40%), linear-gradient(145deg, #202923 0%, #151c18 100%)"
                        : "radial-gradient(circle at 50% 36%, rgba(0, 154, 68, 0.12), transparent 40%), linear-gradient(145deg, #f7faf8 0%, #e8f0ea 100%)",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: { xs: 104, sm: 128 },
                    height: { xs: 124, sm: 152 },
                    borderRadius: 3,
                    display: "grid",
                    placeItems: "center",
                    color: "primary.main",
                    backgroundColor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 18px 44px rgba(20, 60, 35, 0.12)",
                }}
            >
                <InsertDriveFileOutlinedIcon
                    sx={{ fontSize: { xs: 58, sm: 70 } }}
                />
                <Chip
                    label={fileExtension}
                    color="primary"
                    size="small"
                    sx={{
                        position: "absolute",
                        bottom: 12,
                        maxWidth: "calc(100% - 20px)",
                        fontWeight: 800,
                        letterSpacing: "0.04em",
                    }}
                />
            </Box>

            <Typography variant="h5" sx={{ mt: 3, fontWeight: 700 }}>
                Preview unavailable
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    mt: 1,
                    color: "text.secondary",
                    maxWidth: 520,
                    overflowWrap: "anywhere",
                }}
            >
                {file.allowDownloads
                    ? "This file type cannot be displayed in the browser. Download it to open it with an app on your device."
                    : "This file type cannot be displayed in the browser, and downloads are disabled for this file."}
            </Typography>
            {file.type && (
                <Typography
                    variant="caption"
                    sx={{ mt: 1.5, color: "text.disabled" }}
                >
                    {file.type}
                </Typography>
            )}
        </Box>
    );
}

export default function MiscFileViewerPage() {
    const { fileKey } = useParams();

    return (
        <StandardFileViewerPage
            fileKey={fileKey}
            routeSegment="file"
            mediaBackground="background.media"
            renderMedia={(file) => <MiscFilePreview file={file} />}
            renderAdditionalActions={(file, { onDownload }) => (
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadOutlinedIcon />}
                    disabled={!file.allowDownloads}
                    onClick={onDownload}
                    sx={{
                        flex: {
                            xs: "1 1 calc(50% - 4px)",
                            sm: "0 0 auto",
                        },
                    }}
                >
                    Download
                </Button>
            )}
        />
    );
}
